import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "@/components/motion";

const HERO_IMG = "https://images.unsplash.com/photo-1583165278997-0250ea5d72e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxlc3ByZXNzbyUyMG1hY2hpbmUlMjBtb2Rlcm4lMjBvZmZpY2UlMjBzcGVjaWFsdHklMjBjb2ZmZWV8ZW58MHx8fHwxNzkyMjIxMDAyfDA&ixlib=rb-4.1.0&q=85";

const TICKER = [
  { k: "£0", v: "install cost to hosts" },
  { k: "22p", v: "of every cup to charity" },
  { k: "30-day", v: "rolling contract" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({
      x: ((e.clientX - r.left) / r.width - 0.5) * 14,
      y: ((e.clientY - r.top) / r.height - 0.5) * -14,
    });
  };

  return (
    <section ref={ref} onMouseMove={onMouseMove} data-testid="hero-section"
      className="relative min-h-screen bg-[#0B1D16] text-[#FAF9F6] overflow-hidden grain">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#1B5E46]/30 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-[#D97706]/15 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 lg:pt-44 pb-16 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
        <div>
          <MaskedLine delay={0.15}>
            <span className="inline-flex items-center gap-2 text-xs font-mono-brand font-semibold uppercase tracking-[0.22em] text-[#34D399] border border-[#34D399]/30 rounded-full px-4 py-2">
              Coffee for shared spaces
            </span>
          </MaskedLine>
          <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.3}>A better coffee machine.</MaskedLine>
            <MaskedLine delay={0.42}>
              <span><span className="text-[#D97706]">22p</span> of every cup goes to</span>
            </MaskedLine>
            <MaskedLine delay={0.54}>a charity you choose.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7, ease: [0.215, 0.61, 0.355, 1] }}
            className="mt-6 text-base sm:text-lg text-[#FAF9F6]/65 leading-relaxed max-w-xl" data-testid="hero-subtext">
            Zero install fee. Zero hassle. We stock, clean and service your espresso bar — whether you're a workplace, gym, student hall or leisure centre, your people drink specialty bean-to-cup coffee while funding a cause they love.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7, ease: [0.215, 0.61, 0.355, 1] }}
            className="mt-9 flex flex-wrap gap-4">
            <a href="/#enquire" data-testid="hero-cta-host"
              className="group inline-flex items-center gap-2 rounded-full bg-[#D97706] text-white font-bold px-7 py-3.5 text-sm hover:bg-[#F59E0B] transition-colors duration-300">
              Request a machine
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
            <a href="/ladder" data-testid="hero-cta-ladder"
              className="inline-flex items-center rounded-full border border-white/25 text-[#FAF9F6] font-bold px-7 py-3.5 text-sm hover:border-[#34D399] hover:text-[#34D399] transition-colors duration-300">
              Explore the Bean Ladder
            </a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-5 text-xs font-mono-brand uppercase tracking-[0.32em] text-[#93A56B]" data-testid="hero-slogan">
            Good coffee. Great causes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-12 grid grid-cols-3 gap-4 max-w-lg" data-testid="hero-ticker">
            {TICKER.map((t) => (
              <div key={t.v} className="border-t border-white/15 pt-3">
                <p className="font-display font-extrabold text-xl sm:text-2xl text-[#34D399]">{t.k}</p>
                <p className="text-xs text-[#FAF9F6]/55 mt-1">{t.v}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.215, 0.61, 0.355, 1] }}
          className="relative hidden lg:block">
          <motion.div style={{ y: imgY }} className="relative rounded-t-[220px] rounded-b-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
            <img src={HERO_IMG} alt="Specialty espresso machine in a modern office" className="w-full h-[540px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D16]/70 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            animate={{ rotateX: tilt.y, rotateY: tilt.x }}
            transition={{ type: "spring", stiffness: 120, damping: 14 }}
            style={{ transformStyle: "preserve-3d" }}
            data-testid="hero-22p-badge"
            className="absolute -left-10 bottom-16 animate-float bg-[#F7F5F0] text-[#0B1E17] rounded-2xl px-5 py-4 shadow-xl shadow-black/30">
            <p className="font-display font-extrabold text-3xl text-[#D97706]">22p</p>
            <p className="text-xs font-semibold text-[#52665E]">per cup, straight to charity</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
